import ToasterProvider from "components/common/Context/ToasterContext"
import { ErrorBoundary } from "components/common/error"
import { LoadingSpinnerCard } from "components/common/loading"
import { NoDataAndWentWrongHandler } from "components/common/noData"
import { Tooltip } from "components/common/tooltip"
import { notificationTooltipText } from "constants/StringConstants"
import {
  getPendingServiceRequests,
  postTicketApproval
} from "extensions/usoc/api/service"
import PropTypes from "prop-types"
import React, { useContext, useEffect, useState } from "react"
import { useSelector } from "react-redux"
import useSWR from "swr"
import { useExtension } from "utils"
import { useUserAuth } from "utils/hooks"

import { Drawer } from "../drawer"
import { WentWrongCard } from "../noData"
import { NotificationCRTable } from "."
import NotificationRITMTable from "./NotificationRITMTable"

/**
 * Component renders Notification Drawer
 *
 * @example
 * <NotificationsDrawer
 *   isOpen={isNotificationsDrawerOpen}
 *   setSidebarIsOpen={setIsNotificationsDrawerOpen}
 *   setNotificationBadge={setNotificationBadge}
 * />
 */
export default function NotificationsDrawer({
  isOpen,
  setSidebarIsOpen = () => { },
  setNotificationBadge
}) {
  const auth = useUserAuth()
  const projectID = useSelector((state) => {
    return state.settings.currentProject
  })

  const extension = useExtension()
  const host = extension?.config?.env?.clientApiGateway?.URL
  const userEmail = useSelector((state) => {
    return state.user.email
  })
  const enqueueToast = useContext(ToasterProvider)

  // const [isCrNotisExpanded, setIsCrNotisExpanded] = useState(false)
  // const [isRitmNotisExpanded, setIsRitmNotisExpanded] = useState(false)
  const [active, setActive] = useState(0)

  const emailParams = `email=${userEmail}`

  /**
   * Making api call for getting data about total notifications and later diving those request
   * into change request anf RITM notifications
   */
  const { data: pendingServiceRequestsData } = useSWR(
    () =>
      projectID && userEmail ? `/notify/get_snow_events?${emailParams}` : null,
    () => getPendingServiceRequests({ auth, host }, projectID, emailParams),
    { refreshInterval: 100000 }
  )

  async function onApproveClick(state, comments, sysId, type) {
    let notification
    const getNotification = (notificationList) =>
      notificationList.find((notification) => notification.sys_id === sysId)

    if (type === "cr") {
      notification = getNotification(crNotifications)
    } else {
      notification = getNotification(ritmNotifications)
    }

    const response = await postTicketApproval(
      { auth, host },
      notification?.project_id,
      notification?.sys_id,
      {
        state: state,
        comments: comments
      }
    )
    if (!response?.data?.error && response?.data?.result) {
      enqueueToast("success", "Success", "Status updated successfully.")
    } else {
      enqueueToast("failure", "Failure", "Failed to update status.")
    }
  }

  const totalNotifications = Array.isArray(pendingServiceRequestsData?.data)
    ? pendingServiceRequestsData?.data?.reduce((arr, item) => {
      const exists = arr.find((data) => data.sys_id === item.sys_id)
      if (exists) {
        return arr
      }
      return [...arr, item]
    }, [])
    : []

  useEffect(() => {
    if (
      pendingServiceRequestsData?.data &&
      pendingServiceRequestsData?.data.length !== 0
    )
      setNotificationBadge(true)
    else setNotificationBadge(false)
  }, [pendingServiceRequestsData, setNotificationBadge])

  const crNotifications = totalNotifications.filter(
    ({ ticket_number: ticketNumber }) =>
      ticketNumber?.startsWith("CHG") || ticketNumber?.startsWith("REQ")
  )
  const ritmNotifications = totalNotifications.filter(
    ({ ticket_number: ticketNumber }) => ticketNumber?.startsWith("RITM")
  )

  const tabs = [
    `CHANGE REQUESTS (${crNotifications.length})`,
    `RITM (${ritmNotifications.length})`
  ]

  return (
    <Drawer
      isOpen={isOpen}
      onClose={() => setSidebarIsOpen(false)}
      heading={`My Notifications (${totalNotifications.length || 0})`}
      headingTooltip={
        <Tooltip
          className="ml-2"
          imageClassName="notification-tooltip"
          containerClass="text-left pl-2"
          text={notificationTooltipText}
        />
      }
    >
      <div>
        <ErrorBoundary fallback={<WentWrongCard />}>
          <NoDataAndWentWrongHandler
            wentWrongCondition={pendingServiceRequestsData?.error}
          >
            <div className="noti-stat-div">
              {tabs.map((tab, index) => (
                <div
                  className={
                    active === index
                      ? "noti-drawer-tab-btn noti-active-border"
                      : "noti-drawer-tab-btn"
                  }
                  tabIndex={0}
                  onClick={() => setActive(index)}
                  key={index}
                >
                  {tab}
                </div>
              ))}
            </div>
            <React.Suspense fallback={isOpen ? <LoadingSpinnerCard /> : null}>
              {tabs[active].includes("CHANGE REQUESTS") ? (
                <NotificationCRTable
                  notifications={crNotifications}
                  onApproveClick={onApproveClick}
                />
              ) : (
                <NotificationRITMTable
                  notifications={ritmNotifications}
                  onApproveClick={onApproveClick}
                />
              )}
            </React.Suspense>
          </NoDataAndWentWrongHandler>
        </ErrorBoundary>
      </div>
    </Drawer>
  )
}

NotificationsDrawer.propTypes = {
  isOpen: PropTypes.bool,
  setSidebarIsOpen: PropTypes.func,
  setNotificationBadge: PropTypes.func
}
